import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import FieldVisibility from "@mapmycustomers/shared/enum/FieldVisibility";
import SchemaFieldType from "@mapmycustomers/shared/enum/SchemaFieldType";
import { EntityTypesSupportingFieldCustomization } from "@mapmycustomers/shared/types/entity";
import FormLayout, {
  ChildLayout,
  LayoutSchemaField,
} from "@mapmycustomers/shared/types/layout/FormLayout";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Alert, Button, LoadingSpinner, Popconfirm } from "@mapmycustomers/ui";

import InterceptNavigationModal from "@app/component/InterceptNavigationModal";
import EmbeddedModal from "@app/component/modal/EmbeddedModal";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import {
  getEditedChildLayoutId,
  getEditedLayout,
  getEditedLayoutName,
  getEditedLayoutSortedSchema,
  isCreating,
  isEditingActivityLayoutHasEmailVariantLayouts,
  isLayoutEditorHasChanges,
  isLayoutEditorLoading,
  isSaving,
} from "@app/scene/settings/component/FormLayouts/store";
import {
  cancelEditLayout,
  initiateEditLayout,
  saveLayout,
  updateChildLayout,
  updateLayout,
} from "@app/scene/settings/component/FormLayouts/store/actions";
import { getFields } from "@app/scene/settings/component/ManageFields/store";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getDefaultDrawerContainer from "@app/util/ui/getPageContentContainer";

import SchemaMap from "../../type/SchemaMap";
import ActivityRequiredRelationshipAlert from "../ActivityRequiredRelationshipAlert";
import AssociationsModal from "../AssociationsModal";
import Canvas from "../Canvas";
import DeleteLayoutAction from "../DeleteLayoutAction";
import LayoutVariants from "../LayoutVariants";
import Palette from "../Palette";
import PreviewButton from "../PreviewButton";
import SearchBox from "../SearchBox";
import Title from "../Title";
import VariantBanner from "../VariantBanner";

import styles from "./LayoutModal.module.scss";
import { messages } from "./messages";

interface Props {
  activeChildLayout?: ChildLayout["id"];
  cancelEditLayout: typeof cancelEditLayout;
  creating: boolean;
  entityType: EntityTypesSupportingFieldCustomization;
  fields: SchemaField[];
  hasActivityEmailVariantLayouts: boolean;
  hasChanges: boolean;
  id: "create" | FormLayout["id"];
  layout?: FormLayout;
  layoutName?: string;
  loading: boolean;
  onInitiateEditLayout: typeof initiateEditLayout.request;
  onSaveLayout: typeof saveLayout.request;
  onUpdateChildLayout: typeof updateChildLayout;
  onUpdateLayout: typeof updateLayout;
  saving: boolean;
  schema: Array<LayoutSchemaField>;
}

const LayoutModal: React.FC<Props> = ({
  id,
  activeChildLayout,
  cancelEditLayout,
  creating,
  entityType,
  fields,
  hasActivityEmailVariantLayouts,
  hasChanges,
  layout,
  layoutName,
  loading,
  onInitiateEditLayout,
  onSaveLayout,
  onUpdateChildLayout,
  onUpdateLayout,
  saving,
  schema,
}) => {
  const analyticsIssuer = useAnalytics();
  const intl = useIntl();
  const history = useHistory();

  const [assignRolesVisible, showAssignRolesModal, hideAssignRolesModal] = useBoolean();
  const [fieldSearch, setFieldSearch] = useState<string>();
  const [dragging, setDragging] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<null | number>(null);

  const isChildLayout = !!activeChildLayout;
  const activeSchema = useMemo(() => {
    if (activeChildLayout) {
      return layout?.childLayouts?.length
        ? layout?.childLayouts.find((child) => child.id === activeChildLayout)?.schema ?? []
        : [];
    }
    return schema;
  }, [activeChildLayout, layout?.childLayouts, schema]);

  const hasReadOnlyFields = useMemo(
    () =>
      fields.some(
        ({ teamVisibility, visibility }) =>
          visibility === FieldVisibility.PUBLIC ||
          teamVisibility?.some(({ visibility }) => visibility === FieldVisibility.PUBLIC)
      ),
    [fields]
  );

  const onDragStart = useCallback(() => setDragging(true), [setDragging]);

  const onDragEnd = useCallback(
    ({ destination, draggableId, source }: DropResult) => {
      setDragging(false);
      setDragOverIndex(null);

      if (!destination) {
        return;
      }

      if (source.droppableId === "palette") {
        let item: LayoutSchemaField;

        if (draggableId === "divider") {
          item = {
            displayOrder: 0,
            field: "divider",
            fieldType: SchemaFieldType.DIVIDER,
            required: false,
          };
        } else {
          const field = fields.find((field) => field.field === draggableId);
          const fieldType =
            draggableId === "groups"
              ? SchemaFieldType.GROUP
              : draggableId === "files"
              ? SchemaFieldType.FILE
              : field?.fieldType ?? SchemaFieldType.STANDARD;
          item = {
            displayOrder: 0,
            field: field?.field ?? draggableId,
            fieldType,
            required: false,
          };
        }

        const updatedSchema = [...activeSchema];
        updatedSchema.splice(destination.index, 0, item);
        updatedSchema.forEach((row, index) => {
          row.displayOrder = index + 1;
        });

        if (isChildLayout) {
          onUpdateChildLayout({ schema: updatedSchema });
        } else {
          onUpdateLayout({ schema: updatedSchema });
        }
      } else {
        const correction = destination.index > source.index ? 1 : 0;
        const updatedSchema = [...activeSchema];
        const [element] = updatedSchema.splice(source.index, 1);
        updatedSchema.splice(destination.index - correction, 0, element);
        updatedSchema.forEach((row, index) => {
          row.displayOrder = index + 1;
        });

        if (isChildLayout) {
          onUpdateChildLayout({ schema: updatedSchema });
        } else {
          onUpdateLayout({ schema: updatedSchema });
        }
      }
    },
    [
      activeSchema,
      fields,
      isChildLayout,
      setDragging,
      setDragOverIndex,
      onUpdateChildLayout,
      onUpdateLayout,
    ]
  );

  const onDragUpdate = useCallback(
    ({ destination }: DropResult) => {
      if (destination && !isNaN(destination?.index)) {
        setDragOverIndex(destination.index);
      } else {
        setDragOverIndex(null);
      }
    },
    [setDragOverIndex]
  );

  useEffect(() => {
    if (entityType && id) {
      onInitiateEditLayout({ id, entityType });
      setFieldSearch("");
    }
  }, [entityType, onInitiateEditLayout, id, setFieldSearch]);

  const handleExit = useCallback(() => {
    analyticsIssuer?.clicked(["Exit"]);
    hideAssignRolesModal();
    history.push(`${Path.SETTINGS}/${SettingPath.FORMS_CREATE}/${entityType}`);
  }, [analyticsIssuer, entityType, hideAssignRolesModal, history]);

  // cleanup when existing this modal
  useEffect(
    () => () => {
      cancelEditLayout();
    },
    [cancelEditLayout]
  );

  const handleLayoutSaved = useCallback(() => {
    if (creating) {
      showAssignRolesModal();
    }
  }, [creating, showAssignRolesModal]);

  const handleSave = useCallback(() => {
    analyticsIssuer?.clicked(["Save"]);
    onSaveLayout({ callback: handleLayoutSaved });
  }, [analyticsIssuer, onSaveLayout, handleLayoutSaved]);

  const schemaMap: SchemaMap = useMemo(() => {
    if (Array.isArray(activeSchema)) {
      const map = new Map();
      activeSchema.forEach((schemaField) => {
        map.set(schemaField.field, schemaField);
      });
      return map;
    }
    return new Map();
  }, [activeSchema]);

  if (!id || !entityType) {
    return null;
  }

  const nId = Number(id);
  const isSaveButtonDisabled = !hasChanges || !layoutName;
  const isOverLast = dragOverIndex !== null && dragOverIndex >= activeSchema.length;

  return (
    <EmbeddedModal
      className={styles.container}
      getContainer={getDefaultDrawerContainer()}
      open
      zIndex={1}
    >
      <div className={styles.header}>
        <div className={styles.title}>
          <Title />
        </div>
        <div className={styles.actions}>
          {!creating && <DeleteLayoutAction disabled={saving} id={nId} showIcon={false} />}
          <PreviewButton disabled={loading} />
          <Popconfirm
            cancelText={intl.formatMessage(messages.waitBtn)}
            description={intl.formatMessage(messages.confirmTitle)}
            disabled={creating || isSaveButtonDisabled}
            okText={intl.formatMessage(messages.understoodBtn)}
            onConfirm={handleSave}
            overlayClassName={styles.saveOverlay}
            placement="bottomRight"
          >
            <Button
              className={cn({ [styles.saveButton]: hasChanges })}
              disabled={isSaveButtonDisabled}
              onClick={creating ? handleSave : undefined}
              type="primary"
            >
              {hasChanges ? (
                saving ? (
                  <LoadingSpinner className={styles.spinner} mini />
                ) : (
                  intl.formatMessage(messages.save)
                )
              ) : (
                intl.formatMessage(messages.saved, {
                  icon: <FontAwesomeIcon icon={faCheckCircle} />,
                })
              )}
            </Button>
          </Popconfirm>
          <div>
            <Button
              className={styles.exitButton}
              icon={<FontAwesomeIcon icon={faTimes} />}
              iconOnly
              onClick={handleExit}
            />
          </div>
        </div>
      </div>
      <div className={styles.content}>
        {loading ? (
          <LoadingSpinner global />
        ) : (
          <DragDropContext
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
            onDragUpdate={onDragUpdate}
          >
            <div className={styles.sidebar}>
              <div className={styles.sidebarContent}>
                <LayoutVariants />
                <div className={styles.subtitle}>{intl.formatMessage(messages.selectFields)}</div>
                {hasReadOnlyFields && (
                  <Alert
                    className={styles.alert}
                    message={intl.formatMessage(messages.readOnlyFields)}
                    showIcon
                    type="info"
                  />
                )}
                <SearchBox onChange={setFieldSearch} />
                <Palette filter={fieldSearch} schemaMap={schemaMap} />
              </div>
            </div>
            <div className={styles.canvasContainer}>
              <VariantBanner />
              {!hasActivityEmailVariantLayouts && (
                <ActivityRequiredRelationshipAlert
                  className={styles.activityRequiredRelationshipAlert}
                />
              )}
              <div className={cn(styles.canvas, { [styles.smooth]: isOverLast || !dragging })}>
                <Canvas dragOverIndex={dragOverIndex} />
              </div>
            </div>
          </DragDropContext>
        )}
      </div>
      {assignRolesVisible && <AssociationsModal onHide={handleExit} />}
      <InterceptNavigationModal
        onLeave={handleExit}
        shouldCloseOnLeave
        shouldShowPrompt={hasChanges}
      />
    </EmbeddedModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  activeChildLayout: getEditedChildLayoutId(state),
  creating: isCreating(state),
  fields: getFields(state),
  hasActivityEmailVariantLayouts: isEditingActivityLayoutHasEmailVariantLayouts(state),
  hasChanges: isLayoutEditorHasChanges(state),
  layout: getEditedLayout(state),
  layoutName: getEditedLayoutName(state),
  loading: isLayoutEditorLoading(state),
  saving: isSaving(state),
  schema: getEditedLayoutSortedSchema(state),
});

const mapDispatchToProps = {
  cancelEditLayout,
  onInitiateEditLayout: initiateEditLayout.request,
  onSaveLayout: saveLayout.request,
  onUpdateChildLayout: updateChildLayout,
  onUpdateLayout: updateLayout,
};

export default connect(mapStateToProps, mapDispatchToProps)(LayoutModal);
